import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { Layout } from './components/Layout'
import { CustomContentModal as Modal } from '../../../components/CustomContentModal'
import { RegistrationList } from './components/RegistrationList'
import OnboardingMiniTimeline from './components/OnboardingMiniSteps'
import Panel from '../../../components/Panel'
import Button from '../../../components/Form/Button'
import QuickSearch from '../../../components/QuickSearch/QuickSearch'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import Table from '../../../components/Table/Table'
import { Dialog } from '@headlessui/react'
import {
  fetchAllTenantRegistrationRecords,
  previewTenantRegistrationRecords,
  importTenantRegistrationRecords,
  exportDataApi,
  exportZipDataApi,
  getPropertyListApi,
} from '../../../apis/registration'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import type { TenantRegistration, CisrFilterStatus, ProgressFilterStatus, AgreementSubmission } from '../../../types/registration'
import { Paginated } from '../../../apis/types'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import { WARNING_ONLY_FIELDS, PMA_STATUS_BADGE_MAP, PMA_EXPIRING_SOON_DAYS, parseTesaVersion } from './constants'
import { RecordAttributeBadges } from './components/RecordAttributeBadges'

/**
 * The PMA/LOA/CISR column reflects the most recent TESA_SUPPLEMENT event for the record.
 * `agreementSubmissions` is sorted desc by statusDate on the backend, but we explicitly pick the
 * max `statusDate` here (rather than trusting the sort and taking index 0) so this stays correct
 * if the backend sort order ever changes or isn't guaranteed across mixed agreement types. This
 * matches the sort-order-agnostic logic in OnboardingRecordEdit.tsx.
 */
const latestPmaSubmission = (record: TenantRegistration): AgreementSubmission | undefined => {
  const subs = record.invites.ONBOARDING_REGISTRATION?.agreementSubmissions ?? []
  return subs
    .filter((s) => s.agreementType === 'TESA_SUPPLEMENT')
    .reduce<AgreementSubmission | undefined>((latest, curr) => {
      if (!latest) return curr
      return new Date(curr.statusDate).getTime() > new Date(latest.statusDate).getTime() ? curr : latest
    }, undefined)
}

/**
 * Returns the most recent PMA/LOA/CISR event — either a submission (Submitted/Accepted/Rejected/Expired/
 * Expiring) or an externally-uploaded supplement. A given record may have both tracks; we collapse them
 * to a single "what is the current state" view by picking whichever is more recent by date.
 */
type LatestPmaEvent = { kind: 'submission'; submission: AgreementSubmission } | { kind: 'external'; dateSigned: Date }
const latestPmaEvent = (record: TenantRegistration): LatestPmaEvent | undefined => {
  const submission = latestPmaSubmission(record)
  const externalDocs = record.invites.ONBOARDING_REGISTRATION?.externallySignedAgreementDocuments ?? []
  const latestExternal = externalDocs
    .filter((d) => d.type === 'TESA_SUPPLEMENT')
    .map((d) => new Date(d.dateSigned))
    .sort((a, b) => b.getTime() - a.getTime())[0]
  const submissionDate = submission ? new Date(submission.statusDate).getTime() : -Infinity
  const externalDate = latestExternal ? latestExternal.getTime() : -Infinity
  if (submissionDate === -Infinity && externalDate === -Infinity) return undefined
  if (externalDate > submissionDate) return { kind: 'external', dateSigned: latestExternal! }
  return { kind: 'submission', submission: submission! }
}

const DateCell: React.FC<{ iso?: string | null }> = ({ iso }) => {
  if (!iso) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
  return (
    <div>
      <div>{format(d, 'M/d/yy, h:mm a')}</div>
      <div className="text-xs text-slate-400 dark:text-slate-500">{formatDistanceToNow(d, { addSuffix: true })}</div>
    </div>
  )
}

/**
 * Compact mini-timeline preview used in the Progress filter dropdown. Mirrors the bar styling in
 * OnboardingMiniSteps.tsx (OnboardingStep). Seven stages align 1:1 with the mini-timeline:
 *   0 invite → 1 TESA → 2 Supplement → 3 Payment → 4 Complete → 5 Tenant → 6 OneBill
 */
const PROGRESS_STEP_INDEX: Record<ProgressFilterStatus, number> = {
  INVITE_GENERATED: 0,
  TESA_ACCEPTED: 1,
  UTILITY_AUTHORIZED: 2,
  PAYMENT_ACCEPTED: 3,
  REGISTRATION_COMPLETE: 4,
  TENANT_CREATED: 5,
  ONEBILL_CREATED: 6,
}

const ProgressPreview: React.FC<{ stage: ProgressFilterStatus }> = ({ stage }) => {
  const activeIndex = PROGRESS_STEP_INDEX[stage]
  return (
    <div className="flex border-indigo-200 dark:border-indigo-700 border-r-1 mt-1">
      {Array.from({ length: 7 }).map((_, i) => (
        <div
          key={i}
          className={`w-7 h-2.5 border border-indigo-400 dark:border-indigo-500/70 border-r-0 last:border-r first:rounded-l last:rounded-r ${i < activeIndex ? 'bg-indigo-200 dark:bg-indigo-900' : i === activeIndex ? 'bg-indigo-400 dark:bg-indigo-700' : 'bg-transparent'
            }`}
        />
      ))}
    </div>
  )
}

export const Onboarding: React.FC<{}> = () => {
  const dispatch = useAppDispatch()
  const { permissions } = useAppSelector((state) => state.app)
  const hasArchivePermission = permissions?.includes('account:manager:onboarding:archive') ?? false
  const [searchParams, setSearchParams] = useSearchParams()
  const propertyName = searchParams.get('propertyName') ?? undefined
  const pageParam = searchParams.get('page')
  const parsedPage = pageParam != null ? parseInt(pageParam, 10) : 0
  const currentPage = Number.isNaN(parsedPage) ? 0 : parsedPage
  const quickSearchTerm = searchParams.get('search') ?? undefined
  const cisrStatus = (searchParams.get('cisrStatus') as CisrFilterStatus | null) ?? undefined
  const progressStatus = (searchParams.get('progressStatus') as ProgressFilterStatus | null) ?? undefined
  // Archived view is permission-gated. Even if a user manipulates the URL, the backend will only
  // return archived rows if they're authorized at the controller level (and they wouldn't be able
  // to act on the records anyway).
  const archivedOnly = hasArchivePermission && searchParams.get('archived') === 'true'

  const [registrations, setRegistrations] = useState<Paginated<TenantRegistration>>()
  const [isLoadingRegistrations, setIsLoadingRegistrations] = useState(true)
  const [properties, setProperties] = useState<string[]>()
  const [importRegistrations, setImportRegistrations] = useState<TenantRegistration[]>([])
  const [openImportRegistrationIDs, setOpenImportRegistrationIDs] = useState<Set<string>>(() => new Set())
  const [checkedImportIDs, setCheckedImportIDs] = useState<Set<string>>(() => new Set())
  const [isImportRegistrationModalOpen, setIsImportRegistrationModalOpen] = useState<boolean>(false)
  const [importRegistrationModalStep, setImportRegistrationModalStep] = useState<number>(1)
  const [isImportRegistrationFileUploading, setIsImportRegistrationFileUploading] = useState<boolean>(false)
  const [isImportRegistrationFileSaving, setIsImportRegistrationFileSaving] = useState<boolean>(false)
  const hiddenFileInputRef = useRef<HTMLInputElement>(null)
  const fileRef = useRef<File>()

  useEffect(() => {
    const init = async (): Promise<void> => {
      const propertyList = await getPropertyListApi()
      setProperties(propertyList)
    }

    void init()
  }, [])

  const setSearchParamsRef = useRef(setSearchParams)
  setSearchParamsRef.current = setSearchParams

  const updateQueryParams = useCallback((updates: Record<string, string | number | undefined | null>) => {
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(updates)) {
          if (value == null || value === '' || (key === 'page' && value === 0)) {
            next.delete(key)
          } else {
            next.set(key, String(value))
          }
        }
        return next
      },
      { replace: true }
    )
  }, [])

  useEffect(() => {
    let stale = false
    const init = async (): Promise<void> => {
      setIsLoadingRegistrations(true)
      try {
        const registrations = await fetchAllTenantRegistrationRecords({
          page: currentPage,
          propertyName: propertyName?.toString(),
          businessName: quickSearchTerm || undefined,
          cisrStatus,
          progressStatus,
          archivedOnly,
        })
        if (!stale) {
          setRegistrations(registrations)
        }
      } finally {
        if (!stale) {
          setIsLoadingRegistrations(false)
        }
      }
    }

    void init()
    return () => {
      stale = true
    }
  }, [propertyName, quickSearchTerm, currentPage, cisrStatus, progressStatus, archivedOnly])

  const refreshList = useCallback(async (): Promise<void> => {
    setIsLoadingRegistrations(true)
    try {
      const response = await fetchAllTenantRegistrationRecords({
        page: currentPage,
        propertyName: propertyName?.toString(),
        businessName: quickSearchTerm || undefined,
        cisrStatus,
        progressStatus,
        archivedOnly,
      })
      setRegistrations(response)
    } finally {
      setIsLoadingRegistrations(false)
    }
  }, [currentPage, propertyName, quickSearchTerm, cisrStatus, progressStatus, archivedOnly])

  const handlePaginatorClick = useCallback(
    (page: number): void => {
      updateQueryParams({ page })
    },
    [updateQueryParams]
  )

  const handleQuickSearchChange = useCallback(
    (value: string): void => {
      if (value === undefined) {
        return
      }
      updateQueryParams({ search: value || undefined, page: undefined })
    },
    [updateQueryParams]
  )

  const setSelectedPropertyName = (propertyName: string | undefined): void => {
    updateQueryParams({ propertyName, page: undefined })
  }

  const importStats = React.useMemo(() => {
    let errorCount = 0
    let warningFieldCount = 0
    let duplicateCount = 0
    let recommendedFieldCount = 0
    const importableIds: string[] = []

    for (const r of importRegistrations) {
      const hasBlockingError = (r.missingFields ?? []).some((f) => !WARNING_ONLY_FIELDS.has(f))
      if (hasBlockingError) {
        errorCount++
      } else {
        importableIds.push(r.id)
      }
      if ((r.missingFields ?? []).some((f) => WARNING_ONLY_FIELDS.has(f))) {
        recommendedFieldCount++
      }
      if ((r.warningFields ?? []).length > 0) {
        warningFieldCount++
      }
      if (r.isDuplicate) {
        duplicateCount++
      }
    }

    return { errorCount, warningFieldCount, duplicateCount, recommendedFieldCount, importableIds }
  }, [importRegistrations])

  const handleExport = async (selectedPropertyName: string, type: string): Promise<void> => {
    try {
      const objectUrl = await exportDataApi(selectedPropertyName, type)

      const downloadLink = document.createElement('a')
      downloadLink.href = objectUrl
      downloadLink.download = `${selectedPropertyName} - ${type === 'ONBOARDING_REGISTRATION' ? 'Registration' : 'Account'} Invitations.csv`

      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Invitation export is empty',
            description: 'Please adjust your query and try again',
          })
        )
      } else {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Error exporting invitations',
            description: error.response?.data?.message ?? error.response?.message ?? error.message,
          })
        )
      }
    }
  }

  const handleZipExport = async (selectedPropertyName: string, type: string): Promise<void> => {
    try {
      dispatch(
        pushToast({
          message: 'Preparing file(s) for download...',
        })
      )
      const objectUrl = await exportZipDataApi(selectedPropertyName, type)
      const downloadLink = document.createElement('a')
      downloadLink.href = objectUrl
      downloadLink.download = `${selectedPropertyName} - ${type === 'TESA_EXPORT' ? 'TESA' : '<?>'} .zip`

      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)

      dispatch(
        pushToast({
          type: 'success',
          message: 'File(s) downloaded',
        })
      )
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(
          pushToast({
            type: 'error',
            message: 'TESA export is empty',
            description: 'Please adjust your query and try again',
          })
        )
      } else {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Error exporting TESAs',
            description: error.response?.data?.message ?? error.response?.message ?? error.message,
          })
        )
      }
    }
  }

  return (
    <>
      <Layout title="Onboarding">
        <div className="mb-5 flex justify-end">
          <Button
            onClick={() => {
              const fileInput = hiddenFileInputRef.current

              if (fileInput != null) {
                fileInput.value = ''
              }

              fileRef.current = undefined

              setImportRegistrationModalStep(1)
              setIsImportRegistrationModalOpen(true)
            }}
            color="indigo"
          >
            Import from CSV
          </Button>
          {propertyName != null && propertyName !== '' && propertyName !== 'null' && (
            <>
              <Button
                className="ml-2"
                onClick={() => {
                  void handleExport(propertyName, 'ONBOARDING_REGISTRATION')
                }}
              >
                Export Registration Invitations
              </Button>
              <Button
                className="ml-2"
                onClick={() => {
                  void handleExport(propertyName, 'ACCOUNT_SIGNUP')
                }}
              >
                Export Account Invitations
              </Button>
              <Button
                className="ml-2"
                onClick={() => {
                  void handleZipExport(propertyName, 'TESA_EXPORT')
                }}
              >
                Export TESA Docs
              </Button>
            </>
          )}
        </div>
        <Panel header="Filters" className="mb-5">
          <div className="grid grid-auto-flow grid-cols-4 gap-3">
            <QuickSearch title="Business name" initialValue={quickSearchTerm} handleChange={handleQuickSearchChange} />
            <div className="flex">
              <div className="flex flex-col grow">
                <label htmlFor="propertyNames" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Property
                </label>
                {properties !== undefined && properties.length > 0 && (
                  <ComboboxAutocomplete
                    labelPlural="Properties"
                    value={propertyName != null ? propertyName : 'null'}
                    handleChange={setSelectedPropertyName}
                    data={properties?.map((property: string) => ({
                      id: property,
                      name: property,
                    }))}
                  />
                )}
              </div>
            </div>
            <div className="flex">
              <div className="flex flex-col grow">
                <label htmlFor="cisrStatus" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  PMA/LOA/CISR Status
                </label>
                <ComboboxAutocomplete
                  labelPlural="Statuses"
                  value={cisrStatus ?? null}
                  handleChange={(value: string | null) => {
                    updateQueryParams({ cisrStatus: value ?? undefined, page: 0 })
                  }}
                  data={[
                    { id: 'NEEDS_ATTENTION', name: 'Expired or Expiring' },
                    { id: 'EXPIRED', name: 'Expired' },
                    { id: 'EXPIRING_SOON', name: 'Expiring Soon' },
                    { id: 'ACCEPTED', name: 'Accepted' },
                    { id: 'SUBMITTED', name: 'Submitted (pending)' },
                    { id: 'REJECTED', name: 'Rejected' },
                    { id: 'EXTERNAL', name: 'External (uploaded)' },
                    { id: 'NONE', name: 'No PMA/LOA/CISR' },
                  ]}
                />
              </div>
            </div>
            <div className="flex">
              <div className="flex flex-col grow">
                <label htmlFor="progressStatus" className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Progress
                </label>
                <ComboboxAutocomplete
                  labelPlural="Stages"
                  value={progressStatus ?? null}
                  handleChange={(value: string | null) => {
                    updateQueryParams({ progressStatus: value ?? undefined, page: 0 })
                  }}
                  data={[
                    { id: 'INVITE_GENERATED', name: 'Invitation generated', description: <ProgressPreview stage="INVITE_GENERATED" /> },
                    { id: 'TESA_ACCEPTED', name: 'TESA accepted', description: <ProgressPreview stage="TESA_ACCEPTED" /> },
                    { id: 'UTILITY_AUTHORIZED', name: 'Utility authorization', description: <ProgressPreview stage="UTILITY_AUTHORIZED" /> },
                    { id: 'PAYMENT_ACCEPTED', name: 'Pricing & payment terms', description: <ProgressPreview stage="PAYMENT_ACCEPTED" /> },
                    { id: 'REGISTRATION_COMPLETE', name: 'Registration complete', description: <ProgressPreview stage="REGISTRATION_COMPLETE" /> },
                    { id: 'TENANT_CREATED', name: 'Tenant created', description: <ProgressPreview stage="TENANT_CREATED" /> },
                    { id: 'ONEBILL_CREATED', name: 'OneBill account created', description: <ProgressPreview stage="ONEBILL_CREATED" /> },
                  ]}
                />
              </div>
            </div>
            {hasArchivePermission && (
              <div className="flex">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                    checked={archivedOnly}
                    onChange={(e) => {
                      updateQueryParams({ archived: e.target.checked ? 'true' : undefined, page: 0 })
                    }}
                  />
                  Show archived records
                </label>
              </div>
            )}
          </div>
        </Panel>
        {archivedOnly && (
          <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">
            <span>Showing archived onboarding records only.</span>
          </div>
        )}
        {registrations !== undefined &&
          !cisrStatus &&
          !archivedOnly &&
          (() => {
            // Only count records whose LATEST event is an expired/expiring submission. A later-uploaded
            // external supplement supersedes an older expired submission and should not be flagged.
            // Archived records are also skipped — they're out-of-scope for "needs attention" callouts
            // (the banner is suppressed entirely when the archived filter is on; the per-row filter
            // here is defense in case any archived rows ever leak into the active-list response).
            const latestStatusFor = (r: TenantRegistration) => {
              const e = latestPmaEvent(r)
              return e?.kind === 'submission' ? e.submission.status : undefined
            }
            const activeRecords = registrations.results.filter((r) => r.archivedAt == null)
            const expiredCount = activeRecords.filter((r) => latestStatusFor(r) === 'EXPIRED').length
            const expiringCount = activeRecords.filter((r) => latestStatusFor(r) === 'EXPIRING_SOON').length
            if (expiredCount === 0 && expiringCount === 0) return null
            return (
              <div className="mb-3 flex items-center gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-2.5 text-sm">
                <span className="text-amber-800 dark:text-amber-200">
                  {expiredCount > 0 && (
                    <>
                      {expiredCount} record{expiredCount !== 1 ? 's' : ''} {expiredCount !== 1 ? 'have' : 'has'} expired PMA/CISRs.{' '}
                    </>
                  )}
                  {expiringCount > 0 && (
                    <>
                      {expiringCount} record{expiringCount !== 1 ? 's' : ''} {expiringCount !== 1 ? 'are' : 'is'} expiring within {PMA_EXPIRING_SOON_DAYS} days.{' '}
                    </>
                  )}
                </span>
                <button
                  type="button"
                  className="text-amber-700 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-100 font-medium whitespace-nowrap"
                  onClick={() => updateQueryParams({ cisrStatus: 'NEEDS_ATTENTION', page: 0 })}
                >
                  Show records
                </button>
              </div>
            )
          })()}
        <div className="mt-4">
          <Table<TenantRegistration>
            isLoading={isLoadingRegistrations}
            data={
              registrations ?? {
                results: [],
                total: 0,
                page: currentPage,
                resultsPerPage: 20,
              }
            }
            getRowHref={(record) => `/tenant/onboarding/${record.id}`}
            rowClassName={(record) => (record.archivedAt != null ? 'opacity-60' : '')}
            colConfig={[
                  {
                    label: null,
                    render: (record: TenantRegistration) => {
                      if (record.isComplete === false) {
                        return (
                          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        )
                      }
                      return null
                    },
                  },
                  {
                    label: 'Business',
                    columnWidth: 280,
                    contentClassName: 'truncate',
                    render: (record) => {
                      const name = record.tenant?.name?.trim() ?? ''
                      return <span title={name}>{name}</span>
                    },
                  },
                  {
                    label: 'Property',
                    render: (record) => {
                      const parts = record.property?.split(' - ')
                      return (
                        <div>
                          <div>{parts?.[0]}</div>
                          {parts?.[1] && <div className="text-xs text-slate-400 dark:text-slate-500">{parts[1]}</div>}
                        </div>
                      )
                    },
                  },
                  {
                    label: 'Created',
                    render: (record) => <DateCell iso={record.createdAt} />,
                  },
                  {
                    label: 'Last Updated',
                    render: (record) => <DateCell iso={record.lastUpdatedAt} />,
                  },
                  {
                    label: 'PMA/LOA/CISR',
                    render: (record) => {
                      const latest = latestPmaEvent(record)
                      if (!latest) {
                        return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      }
                      if (latest.kind === 'external') {
                        return (
                          <div className="flex flex-wrap gap-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
                              External
                            </span>
                          </div>
                        )
                      }
                      const badge = PMA_STATUS_BADGE_MAP[latest.submission.status]
                      if (!badge) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      return (
                        <div className="flex flex-wrap gap-1">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badge.classes}`}>{badge.label}</span>
                        </div>
                      )
                    },
                  },
                  {
                    label: 'TESA',
                    render: (record) => {
                      const raw = record.invites.ONBOARDING_REGISTRATION?.tesaVersion
                      const isExternalTesa = record.invites.ONBOARDING_REGISTRATION?.externallySignedAgreementDocuments?.some((d: any) => d.type === 'TESA')
                      if (raw == null) return <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                      const parsed = parseTesaVersion(raw)
                      if (parsed.isCustom) {
                        return (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
                            External
                          </span>
                        )
                      }
                      const versionClasses = parsed.isLegacy
                        ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200'
                        : parsed.isCurrent
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                      return (
                        <div className="flex flex-wrap gap-1">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${versionClasses}`}>{parsed.display}</span>
                          {isExternalTesa && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200">
                              External
                            </span>
                          )}
                        </div>
                      )
                    },
                  },
                  {
                    label: 'Attributes',
                    render: (record) => <RecordAttributeBadges record={record} size="list" />,
                  },
                  {
                    label: 'Utility',
                    render: (record) => record.utility,
                  },
                  {
                    label: 'Progress',
                    render: (record) => (
                      <OnboardingMiniTimeline
                        invites={record.invites}
                        tenantName={record.tenant?.name}
                        isNational={record.isNational === true}
                      />
                    ),
                  },
                ]}
            handlePaginatorClick={handlePaginatorClick}
          />
        </div>
      </Layout>
      <Modal
        isOpen={isImportRegistrationModalOpen}
        onClose={() => {
          if (isImportRegistrationFileUploading || isImportRegistrationFileSaving) {
            return
          }

          setIsImportRegistrationModalOpen(false)
        }}
      >
        <div className={`${importRegistrationModalStep === 1 ? 'max-w-md' : 'max-w-5xl'} p-6 text-left bg-white dark:bg-slate-800`}>
          {importRegistrationModalStep === 1 && (
            <>
              <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-slate-900 dark:text-slate-100">
                Import Tenant Feed Data
              </Dialog.Title>
              <div className="mt-4 text-sm">
                <p className="text-slate-500 dark:text-slate-400">Select a CSV file containing the tenant feed data meant for preview and storage.</p>
                <p className="my-4 text-slate-500 dark:text-slate-400">
                  Supported columns <span className="text-red-500">*</span> = required:
                </p>
                <ul className="list-disc columns-2 text-xs whitespace-nowrap text-slate-500 dark:text-slate-400">
                  <li>
                    Contact ID <span className="text-red-500">*</span>
                  </li>
                  <li>
                    Program: Program Name <span className="text-red-500">*</span>
                  </li>
                  <li>
                    First Name <span className="text-red-500">*</span>
                  </li>
                  <li>
                    Last Name <span className="text-red-500">*</span>
                  </li>
                  <li>
                    Account Name <span className="text-red-500">*</span>
                  </li>
                  <li>Utility Account Name</li>
                  <li>Utility Account ID</li>
                  <li>
                    Billing email address <span className="text-red-500">*</span>
                  </li>
                  <li>Phone</li>
                  <li>Service Address Street</li>
                  <li>Service Address City</li>
                  <li>Service Address State</li>
                  <li>Service Address Zip</li>
                  <li>Billing Address Street</li>
                  <li>Billing Address City</li>
                  <li>Billing Address State</li>
                  <li>Billing Address Zip</li>
                  <li>Meter Number</li>
                  <li>kWh/yr</li>
                  <li>
                    Tariff Object: Tariff <span className="text-red-500">*</span>
                  </li>
                  <li>Service ID</li>
                  <li>Utility: Utility</li>
                  <li>
                    Utility Record ID <span className="text-red-500">*</span>
                  </li>
                  <li>Energy Bill Discount %</li>
                  <li>ProjectCo of Program</li>
                  <li>
                    Program Nickname <span className="text-red-500">*</span>
                  </li>
                  <li>
                    Account ID <span className="text-red-500">*</span>
                  </li>
                  <li>Tenant Type</li>
                  <li>CCA</li>
                  <li>
                    Program ID <span className="text-red-500">*</span>
                  </li>
                  <li>Landlord Entity Name</li>
                  <li>
                    Meter Data ID <span className="text-red-500">*</span>
                  </li>
                  <li>
                    Property Name <span className="text-red-500">*</span>
                  </li>
                  <li>Parent Account Record ID</li>
                  <li>Parent Payment Terms (Days)</li>
                  <li>Business Entity Name</li>
                  <li>Business Entity Type</li>
                  <li>State of Formation</li>
                </ul>
              </div>
              <div className="mt-4 flex justify-end">
                <Button type="button" color="slate" disabled={isImportRegistrationFileUploading} onClick={() => setIsImportRegistrationModalOpen(false)}>
                  Cancel
                </Button>
                <div className="ml-2">
                  <Button isSaving={isImportRegistrationFileUploading} onClick={() => hiddenFileInputRef.current?.click()}>
                    Choose File...
                  </Button>
                  <input
                    ref={hiddenFileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={(event) => {
                      async function init(): Promise<void> {
                        const file = event.target.files?.[0]
                        if (file === undefined) {
                          return
                        }

                        fileRef.current = file

                        setIsImportRegistrationFileUploading(true)

                        let importRegistrations: TenantRegistration[]

                        try {
                          importRegistrations = await previewTenantRegistrationRecords(file)

                          setImportRegistrations(importRegistrations)

                          const ids = importRegistrations.map(({ id }) => id)
                          setOpenImportRegistrationIDs(new Set(ids))
                          const importableIds = importRegistrations
                            .filter((r) => (r.missingFields ?? []).filter((f) => !WARNING_ONLY_FIELDS.has(f)).length === 0)
                            .map(({ id }) => id)
                          setCheckedImportIDs(new Set(importableIds))
                          setImportRegistrationModalStep(2)
                        } catch (error: any) {
                          dispatch(
                            pushToast({
                              type: 'error',
                              message: `Failed to import feed`,
                              description: error.response?.data?.message ?? error.response?.message ?? error.message,
                            })
                          )
                        } finally {
                          setIsImportRegistrationFileUploading(false)
                        }
                      }

                      void init()
                    }}
                    className="hidden"
                  />
                </div>
              </div>
            </>
          )}
          {importRegistrationModalStep === 2 && (
            <>
              <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-slate-900 dark:text-slate-100">
                Import Tenant Feed Data
              </Dialog.Title>
              <div className="mt-4">
                {isImportRegistrationFileUploading ? (
                  <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Processing import...
                  </div>
                ) : (
                  <>
                    {importStats.errorCount > 0 && (
                      <div className="mb-3 rounded-md bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 p-3">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {importStats.errorCount} record(s) are missing required fields and will not be imported.
                        </p>
                      </div>
                    )}
                    {importStats.recommendedFieldCount > 0 && (
                      <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {importStats.recommendedFieldCount} record(s) are missing recommended fields (Business Entity Name, Business Entity Type, State of
                          Formation) but will still be imported.
                        </p>
                      </div>
                    )}
                    {importStats.warningFieldCount > 0 && (
                      <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {importStats.warningFieldCount} record(s) have utility account number format issues. Please verify before importing.
                        </p>
                      </div>
                    )}
                    {importStats.duplicateCount > 0 && (
                      <div className="mb-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3">
                        <p className="text-sm text-amber-800 dark:text-amber-300">
                          {importStats.duplicateCount} record(s) may already exist. Duplicates are highlighted below.
                        </p>
                      </div>
                    )}
                    <RegistrationList
                      registrations={importRegistrations}
                      openRegistrationIDs={openImportRegistrationIDs}
                      areActionsHidden={true}
                      checkedIDs={checkedImportIDs}
                      onCheckedChange={(id: string) => {
                        const next = new Set(checkedImportIDs)
                        if (next.has(id)) {
                          next.delete(id)
                        } else {
                          next.add(id)
                        }
                        setCheckedImportIDs(next)
                      }}
                      onCheckedAllChange={(checked: boolean) => {
                        if (checked) {
                          setCheckedImportIDs(new Set(importStats.importableIds))
                        } else {
                          setCheckedImportIDs(new Set())
                        }
                      }}
                      onItemClick={(idx) => {
                        setOpenImportRegistrationIDs((prev) => {
                          const next = new Set(prev)
                          if (next.has(idx)) {
                            next.delete(idx)
                          } else {
                            next.add(idx)
                          }
                          return next
                        })
                      }}
                      onItemActionsClick={(index, action) => console.log(index, action)}
                    />
                  </>
                )}
              </div>
              {(() => {
                const checkedCount = checkedImportIDs.size
                return (
                  <div className="mt-4">
                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-2 text-right">Only checked records will be imported.</p>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {checkedCount} of {importRegistrations.length} record(s) will be imported
                        {importStats.errorCount > 0 && <span className="text-red-600 dark:text-red-400"> ({importStats.errorCount} invalid)</span>}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          color="slate"
                          disabled={isImportRegistrationFileUploading}
                          onClick={() => setIsImportRegistrationModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          disabled={checkedCount === 0}
                          isSaving={isImportRegistrationFileSaving}
                          onClick={() => {
                            async function init(): Promise<void> {
                              if (fileRef.current === undefined) {
                                return
                              }

                              setIsImportRegistrationFileSaving(true)

                              const excludeBusinessNames = importRegistrations.filter((r) => !checkedImportIDs.has(r.id)).map((r) => r.tenant.name)

                              const checkedNames = importRegistrations.filter((r) => checkedImportIDs.has(r.id)).map((r) => r.tenant.name)
                              const nameSet = new Set<string>()
                              const duplicateNames = new Set<string>()
                              for (const name of checkedNames) {
                                if (nameSet.has(name)) duplicateNames.add(name)
                                nameSet.add(name)
                              }
                              if (duplicateNames.size > 0) {
                                const proceed = window.confirm(
                                  `Warning: ${duplicateNames.size} business name(s) appear more than once in the selected records (${[...duplicateNames].join(', ')}). The import API uses business name for exclusion, so records with the same name cannot be individually excluded. Continue?`
                                )
                                if (!proceed) {
                                  setIsImportRegistrationFileSaving(false)
                                  return
                                }
                              }

                              try {
                                const registrationRecords = await importTenantRegistrationRecords(fileRef.current!, excludeBusinessNames)
                                setIsImportRegistrationModalOpen(false)

                                await refreshList()

                                const propertyList = await getPropertyListApi()
                                setProperties(propertyList)

                                dispatch(
                                  pushToast({
                                    type: 'success',
                                    message: 'Feed imported',
                                  })
                                )

                                if (registrationRecords[0]?.property) {
                                  setSelectedPropertyName(registrationRecords[0].property)
                                }
                              } catch (error: any) {
                                dispatch(
                                  pushToast({
                                    type: 'error',
                                    message: `Failed to import feed`,
                                    description: error.response?.data?.message ?? error.response?.message ?? error.message,
                                  })
                                )
                              } finally {
                                setIsImportRegistrationFileSaving(false)
                              }
                            }

                            void init()
                          }}
                        >
                          Import {checkedCount} record{checkedCount !== 1 ? 's' : ''}
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })()}
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export default Onboarding
